import { apiClient } from '../api-client'
import { DashboardStats, Task, Project, Milestone, Notification, TimeEntry } from '@/types'

export interface AdminProjectPortfolioRow {
  project_id: string
  project_name: string
  project_status: string
  project_progress: number
  milestones_total: number
  milestones_completed: number
  tasks_total: number
  tasks_completed: number
  tasks_overdue: number
  bugs_total: number
  bugs_completed: number
}

export interface WorkSummaryBucket {
  assigned_this_week: number
  completed_this_week: number
  pending: number
  overdue: number
  open_total: number
  hold_total: number
  reopened_total: number
}

export interface MyWorkSummary {
  user_id: string | null
  employee_name: string | null
  week_start: string
  week_end: string
  generated_at: string
  tasks: WorkSummaryBucket
  bugs: WorkSummaryBucket
  attention: {
    pending_tasks: number
    hold_tasks: number
    pending_bugs: number
    hold_bugs: number
    pending_reopened_bugs: number
  }
  performance: {
    week_score: number | null
    month_score: number | null
  }
}

export interface AdminWorkSummaryRow {
  user_id: string
  employee_name: string
  email: string
  role: string
  tasks: WorkSummaryBucket
  bugs: WorkSummaryBucket
  performance_score_week: number | null
  performance_score_month: number | null
  last_activity_at: string | null
  highlight_low_workload: boolean
  total_open_items: number
}

export interface AdminWorkSummaryResponse {
  week_start: string
  week_end: string
  generated_at: string
  page: number
  limit: number
  total: number
  total_pages: number
  low_workload_threshold: number
  filters: {
    user_id?: string | null
    project_id?: string | null
    team_id?: string | null
  }
  rows: AdminWorkSummaryRow[]
}

export interface AdminWorkSummaryParams {
  user_id?: string
  project_id?: string
  team_id?: string
  page?: number
  limit?: number
  low_workload_threshold?: number
}

export interface AttentionPeriod {
  capacity_hours: number
  tracked_hours: number
  active_timer_hours: number
  unclocked_hours: number
  active_timer_count: number
  is_tracking: boolean
  active_work_count?: number
  active_work_tasks?: number
  active_work_bugs?: number
  is_within_work_window?: boolean
  is_currently_unclocked?: boolean
  should_prompt?: boolean
}

export interface DashboardAttention {
  generated_at: string
  timezone: string
  overdue: {
    total: number
    tasks: number
    bugs: number
    reminder_created: boolean
    items: Array<{
      id: string
      type: 'task' | 'bug'
      title: string
      project_name: string
      due_date: string
      action_url: string
    }>
  }
  time: {
    today: AttentionPeriod
    week: AttentionPeriod
    month: AttentionPeriod
    reminder_threshold_hours: number
  }
}

// Dashboard API
export const dashboardApi = {
  getStats: (): Promise<DashboardStats> =>
    apiClient.get('/dashboard/stats'),

  getAttentionSummary: (): Promise<DashboardAttention> =>
    apiClient.get('/dashboard/attention/me'),

  getMyTasks: (limit = 10): Promise<Task[]> =>
    apiClient.get('/dashboard/my-tasks', { params: { limit } }),

  getMyBugs: (limit = 10): Promise<any[]> =>
    apiClient.get('/dashboard/my-bugs', { params: { limit } }),

  getMyProjects: (limit = 10): Promise<Project[]> =>
    apiClient.get('/dashboard/my-projects', { params: { limit } }),

  getMyMilestones: (limit = 10): Promise<Milestone[]> =>
    apiClient.get('/dashboard/my-milestones', { params: { limit } }),

  getRecentNotifications: (limit = 10): Promise<Notification[]> =>
    apiClient
      .get('/notifications', {
        params: { unread_only: false, skip: 0, limit }
      })
      .then((rows: any[]) =>
        (rows || []).map((row: any) => ({
          id: String(row.id),
          type: String(row.event_type || row.category || 'system_event'),
          category: String(row.category || 'system'),
          eventType: String(row.event_type || 'system_event'),
          actionUrl: row.action_url || undefined,
          relatedType:
            row?.metadata?.task_id ? 'task' :
            row?.metadata?.bug_id ? 'bug' :
            row?.metadata?.ticket_id ? 'ticket' :
            row?.metadata?.project_id ? 'project' :
            row?.metadata?.milestone_id ? 'milestone' :
            undefined,
          title: row.title,
          message: row.message,
          read: Boolean(row.read_at) || row.status === 'read',
          userId: String(row.recipient_id),
          relatedId:
            row?.metadata?.task_id ||
            row?.metadata?.bug_id ||
            row?.metadata?.ticket_id ||
            row?.metadata?.project_id ||
            row?.metadata?.milestone_id,
          createdAt: row.created_at
        }))
      ),

  getWeeklyTimesheet: (): Promise<TimeEntry[]> =>
    apiClient.get('/dashboard/timesheet/week'),

  getWeeklyHours: (): Promise<{assigned: number, actual: number, week_start: string, week_end: string}> =>
    apiClient.get('/dashboard/hours/weekly'),

  getMonthlyHours: (): Promise<{assigned: number, actual: number, month_start: string, month_end: string}> =>
    apiClient.get('/dashboard/hours/monthly'),

  getTasksBugsRatio: (): Promise<{tasks: number, bugs: number, ratio: number}> =>
    apiClient.get('/dashboard/ratios'),

  getTaskPriorityBreakdown: (): Promise<{urgent: number, high: number, medium: number, low: number}> =>
    apiClient.get('/dashboard/priorities'),

  getOverdueItems: (): Promise<{overdue_tasks: number, overdue_bugs: number}> =>
    apiClient.get('/dashboard/overdue'),

  getAssignedProjects: (): Promise<Array<{id: string, name: string, role: string, status: string}>> =>
    apiClient.get('/dashboard/projects/assigned'),

  getSystemHealth: (): Promise<{
    database_status: string;
    total_users: number;
    active_projects: number;
    system_uptime_hours: number;
    server_status: string;
    last_backup: string;
  }> =>
    apiClient.get('/dashboard/system-health'),

  getTaskMetrics: (period?: string): Promise<{
    period: string;
    total_tasks: number;
    completed_tasks: number;
    pending_tasks: number;
    overdue_tasks: number;
    unassigned_tasks: number;
    reopened_tasks: number;
    completion_rate: number;
    priority_breakdown: Record<string, number>;
    status_breakdown: Record<string, number>;
  }> =>
    apiClient.get('/dashboard/tasks/metrics', { params: { period } }),

  getBugMetrics: (period?: string): Promise<{
    period: string;
    total_bugs: number;
    resolved_bugs: number;
    open_bugs: number;
    critical_bugs: number;
    unassigned_bugs: number;
    overdue_bugs: number;
    resolution_rate: number;
    average_resolution_days: number;
    severity_breakdown: Record<string, number>;
    status_breakdown: Record<string, number>;
  }> =>
    apiClient.get('/dashboard/bugs/metrics', { params: { period } }),

  getProjectMetrics: (status_filter?: string): Promise<{
    total_projects: number;
    active_projects: number;
    completed_projects: number;
    on_hold_projects: number;
    overdue_projects: number;
    completion_rate: number;
    average_duration_days: number;
    priority_breakdown: Record<string, number>;
  }> =>
    apiClient.get('/dashboard/projects/metrics', { params: { status_filter } }),

  getMilestoneMetrics: (period?: string): Promise<{
    period: string;
    total_milestones: number;
    completed_milestones: number;
    pending_milestones: number;
    overdue_milestones: number;
    upcoming_milestones: number;
    completion_rate: number;
    average_completion_percentage: number;
    status_breakdown: Record<string, number>;
  }> =>
    apiClient.get('/dashboard/milestones/metrics', { params: { period } }),

  getAdminProjectPortfolioSummary: (limit = 100, status_filter = 'active'): Promise<{
    projects: AdminProjectPortfolioRow[]
    total_active_projects: number
    status_filter: string
  }> =>
    apiClient.get('/dashboard/projects/portfolio-summary', { params: { limit, status_filter } }),

  getMyWorkSummary: (): Promise<MyWorkSummary> =>
    apiClient.get('/dashboard/work-summary/me'),

  getAdminWorkSummary: (params?: AdminWorkSummaryParams): Promise<AdminWorkSummaryResponse> =>
    apiClient.get('/dashboard/work-summary/users', { params }),
}
